import { initializeApollo, initializeApolloWithoutToken } from '../lib/apollo-client';
import axios from 'axios';
import requestHandler from '../requestHandler';
import {
  FETCH_USER_TRACKING,
  FETCH_USER_TRACKING_RANGE,
  QUERY_FORGOT_PASSWORD_PAGE,
  QUERY_LOGIN_PAGE,
  QUERY_REGISTER_PAGE,
  MUTATION_RESET_PASSWORD,
  MUTATION_LOGIN_TRACKER,
  CreateLoginTrackerMutation,
  FetchLoginTrackerQuery,
  FetchUserTrackingQuery,
  ForgotPasswordPageQuery,
  LoginPageQuery,
  RegisterPageQuery,
  ResetPasswordMutation,
  UsersPermissionsUser,
} from '@edwin-group/people-graphql';
import { getAuthentication } from '@/hooks/useAuthentication';
import { getAccessToken } from './token-store';

export async function login({
  email,
  password,
  rememberMe,
  recaptchaToken,
}: {
  email: string;
  password: string;
  rememberMe?: boolean;
  recaptchaToken?: string | null;
}) {
  let data, error;
  // The lockout is signalled by the status and the Retry-After header, not by the
  // body - the body stays generic on purpose. Both have to survive this boundary
  // or the login page has nothing to tell the user.
  let status: number | undefined;
  let retryAfterSec: number | undefined;

  try {
    const { data: response } = await axios<LoginResponse>({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/local`,
      data: { email, password, rememberMe, recaptchaToken },
      // Required for the browser to store the httpOnly refresh cookie that the
      // login response sets. Without it the session cannot outlive the 15-minute
      // access token.
      withCredentials: true,
    });

    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
      status = err.response.status;
      retryAfterSec = parseRetryAfter(err.response.headers?.['retry-after']);
    } else {
      // Network / CORS failures have no response. Without this the login page
      // treats an empty result as an "unexpected sign-in response" and shows
      // the CMS placeholder toast instead of a useful message.
      error = {
        message: 'Unable to reach the sign-in service. Check your connection and try again.',
      };
    }
  }

  return { data, error, status, retryAfterSec };
}

function parseRetryAfter(header: unknown): number | undefined {
  const seconds = Number(header);

  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : undefined;
}

/**
 * Destroys the session server-side.
 *
 * Until this existed, logging out only cleared localStorage: the token stayed
 * valid on the server, so anyone who had captured it kept full access. The
 * backend deletes every session row for the user and clears the refresh cookie.
 *
 * `withCredentials` matters - the refresh cookie must be sent so the server can
 * clear it, and it is httpOnly, so this is the only way it travels.
 */
export async function logoutSession() {
  let error;

  try {
    await axios({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/logout`,
      data: {},
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
  } catch (err) {
    // Deliberately swallowed. The caller clears local state regardless: an
    // already-expired token, or a dropped network, must not leave someone
    // looking logged in after they clicked "log out".
    console.error('Server-side logout failed; clearing local session anyway', err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }

  return { error };
}

/**
 * The password step (/api/auth/local) never returns a session directly: MFA is
 * mandatory. It returns one of:
 *  - { mfaRequired, mfaToken }      -> user is enrolled, verify a TOTP code
 *  - { mfaSetupRequired, mfaToken } -> user must enrol before proceeding
 * The `mfaToken` is short-lived and only authorises the matching MFA step.
 */
export interface SessionResponse {
  token: string;
  user: UsersPermissionsUser;
}
export interface LoginResponse extends Partial<SessionResponse> {
  mfaRequired?: boolean;
  mfaSetupRequired?: boolean;
  mfaToken?: string;
}

async function postAuth<T>(path: string, body: Record<string, unknown>) {
  let data: T | undefined;
  let error;
  try {
    const { data: response } = await axios<T>({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}${path}`,
      data: body,
      // The session-issuing MFA steps (/mfa/verify, /mfa/enable) set the
      // httpOnly refresh cookie via Set-Cookie. On the cross-origin call from
      // the SPA the browser only stores that cookie when the request carries
      // credentials — without this the cookie is dropped, every later
      // /api/auth/refresh answers "Missing refresh token", and the session dies
      // the moment the 15-minute in-memory access token needs renewing.
      withCredentials: true,
    });
    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }
  return { data, error };
}

/** Start enrolment: returns the otpauth URL, a QR data-URL and the raw secret. */
export async function setupMfa(mfaToken: string) {
  return postAuth<{ otpauthUrl: string; qrDataUrl: string | null; secret: string }>('/api/auth/mfa/setup', {
    mfaToken,
  });
}

/** Confirm enrolment with a code; returns a full session plus one-time backup codes. */
export async function enableMfa(mfaToken: string, code: string) {
  return postAuth<SessionResponse & { backupCodes: string[] }>('/api/auth/mfa/enable', {
    mfaToken,
    code,
  });
}

/** Verify the second factor for an enrolled user; returns a full session. */
export async function verifyMfa(mfaToken: string, code: string) {
  return postAuth<SessionResponse>('/api/auth/mfa/verify', { mfaToken, code });
}

/**
 * Self-service MFA recovery — step 1: ask for an emailed reset code. Gated by the
 * `mfa-challenge` token (so only someone who passed the password step can ask).
 * Always resolves with a generic message; never reveals whether the email matched.
 */
export async function requestMfaReset(mfaToken: string, email: string) {
  return postAuth<{ message: string }>('/api/auth/mfa/reset/request', { mfaToken, email });
}

/**
 * Self-service MFA recovery — step 2: verify the emailed code. On success the
 * account's MFA is cleared and a fresh `mfa-setup` token is returned so the user
 * can re-enrol.
 */
export async function verifyMfaReset(email: string, code: string) {
  return postAuth<{ mfaSetupRequired: boolean; mfaToken: string }>('/api/auth/mfa/reset/verify', {
    email,
    code,
  });
}

export async function register(user: {
  email: string;
  password: string;
  schoolCode: string;
  forename: string;
  surname: string;
}) {
  let data, error;

  try {
    const { data: response } = await axios({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/register`,
      data: {
        email: user.email,
        password: user.password,
        schoolCode: user.schoolCode,
        device: 'web',
        forename: user.forename,
        surname: user.surname,
      },
    });

    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }

  return { data, error };
}

export async function updateProfile(id: string, profile: string) {
  return Promise.resolve({ id, profile });
  //   const apolloClient = initializeApolloWithoutToken();

  //   return requestHandler(
  //     apolloClient.mutate<UpdateUserMutation>({
  //       mutation: MUTATION_UPDATE_USER,
  //       variables: {
  //         id,
  //         input: {
  //           profile,
  //         },
  //       },
  //     }),
  //   );
}

export async function forgotPassword(email: string) {
  let data, error;

  try {
    const { data: response } = await axios({
      method: 'post',
      url: `${import.meta.env.VITE_API_URL}/api/auth/forgot-password`,
      data: { email, device: 'web' },
    });

    data = response;
  } catch (err) {
    console.error(err);
    // eslint-disable-next-line import/no-named-as-default-member
    if (axios.isAxiosError(err) && err.response) {
      error = err.response?.data?.error;
    }
  }

  return { data, error };
}

export async function resetPassword({
  password,
  passwordConfirmation,
  code,
}: {
  password: string;
  passwordConfirmation: string;
  code: string;
}) {
  const apolloClient = initializeApolloWithoutToken();

  return requestHandler(
    apolloClient.mutate<ResetPasswordMutation>({
      mutation: MUTATION_RESET_PASSWORD,
      variables: {
        password,
        passwordConfirmation,
        code,
      },
    }),
  );
}

export async function getForgotPasswordPage() {
  const apolloClient = initializeApolloWithoutToken();

  const { data, error } = await requestHandler(
    apolloClient.query<ForgotPasswordPageQuery>({
      query: QUERY_FORGOT_PASSWORD_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return { page: data?.forgotPassword?.data?.attributes, error };
}

export async function getLoginPage() {
  const apolloClient = initializeApolloWithoutToken();

  const { data, error } = await requestHandler(
    apolloClient.query<LoginPageQuery>({
      query: QUERY_LOGIN_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return { page: data?.loginPage?.data?.attributes, error };
}

export async function getRegisterPage() {
  const apolloClient = initializeApolloWithoutToken();

  const { data, error } = await requestHandler(
    apolloClient.query<RegisterPageQuery>({
      query: QUERY_REGISTER_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return { page: data?.registerPage?.data?.attributes, error };
}

export async function updateSchoolCode(id: string, schoolCode: string) {
  return Promise.resolve({ id, schoolCode });
  //   const apolloClient = initializeApolloWithoutToken();

  //   return requestHandler(
  //     apolloClient.mutate<UpdateUserMutation>({
  //       mutation: MUTATION_UPDATE_USER,
  //       variables: {
  //         id,
  //         input: {
  //           schoolCode,
  //         },
  //       },
  //     }),
  //   );
}

export async function loginTracker(id: string) {
  if (!id) {
    return null;
  }

  try {
    const apolloClient = initializeApollo();

    return requestHandler(
      apolloClient.mutate<CreateLoginTrackerMutation>({
        mutation: MUTATION_LOGIN_TRACKER,
        variables: {
          input: {
            staff: id,
          },
        },
      }),
    );
  } catch (error) {
    /* empty */
  }
}

export const getLoginRange = async (startDay: Date, endDay: Date) => {
  const apolloClient = initializeApollo();

  const inputData = {
    endDay: endDay.toISOString().split('T')[0],
    startDay: startDay.toISOString().split('T')[0],
    staffId: getAuthentication()?.user?.documentId,
  };

  const { data } = await requestHandler(
    apolloClient.query<FetchLoginTrackerQuery>({
      query: FETCH_USER_TRACKING_RANGE,
      variables: {
        ...inputData,
      },
    }),
  );

  return data?.loginTrackers?.data;
};

export const fetchUserTracking = async () => {
  const apolloClient = initializeApollo();

  const { data } = await requestHandler(
    apolloClient.query<FetchUserTrackingQuery>({
      query: FETCH_USER_TRACKING,
      fetchPolicy: 'cache-first',
    }),
  );

  return data?.getUserTrackingData;
};
